import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Link as RouterLink } from 'react-router';

import { IconBadge } from '@/components/atoms';
import { type Service, servicePath } from '@/content/services';

import { ServiceCostChip } from './ServiceCostChip';

type Props = {
  service: Service;
  /**
   * Draw the summary and the outcome as well as the tagline. The catalogue
   * has the width for all three; the siblings band at the foot of a service
   * page does not, and a six-line card there would compete with the page it
   * is closing.
   */
  detailed?: boolean;
  /**
   * The card's heading level, which depends entirely on what encloses it and
   * so cannot be decided here.
   *
   * On the hub the card sits under a pillar's `h3`, so it is an `h4`. In the
   * siblings band it sits directly under that band's `h2`, so it is an `h3` —
   * and hard-coding `h4` there skipped a level, which is the one thing a
   * heading outline must not do: assistive technology uses the levels to
   * build the page's structure, and a gap reads as a missing section.
   */
  level?: 'h3' | 'h4';
};

/**
 * One service, as a card that is entirely a link.
 *
 * The whole card is the anchor rather than a "Learn more" link inside it. Two
 * reasons, and the second is the one that matters: a card with a link in it
 * gives a pointer a small target inside a large clickable-looking area, and a
 * screen reader a link whose accessible name is "Learn more" seven times
 * over. One anchor per card means the name is the service, and the target is
 * the card.
 *
 * ## The footer
 *
 * The cost chip was added 2026-10-21 and it is the card's most useful line.
 * A grid of service cards answers "what is this" seven times and "do I pay
 * for it" never, which is the question that decides whether a visitor reads
 * the second card at all — and a reader who finds out on the detail page that
 * a consultation was free has already spent the attention that would have
 * been worth spending somewhere else.
 *
 * It sits below a divider with the arrow, rather than beside the title. Up
 * beside the title it competed with the service's name for the first glance;
 * down here it is read at the moment the eye is already leaving the card,
 * which is when the question is actually asked.
 *
 * The arrow is decorative and the hover lift is the only animation — no
 * library, per the repo's card rules. Height comes from the grid rather than
 * from a fixed value, so a three-line tagline and a one-line tagline sit in
 * cards of the same height without either being padded to a guess.
 */
export function ServiceCard({ service, detailed = false, level = 'h4' }: Props) {
  return (
    <Box
      component={RouterLink}
      to={servicePath(service)}
      prefetch="intent"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        gap: 4,
        p: { xs: 5, md: 6 },
        borderRadius: 3,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        textDecoration: 'none',
        color: 'inherit',
        transition: 'border-color 250ms ease, transform 250ms ease, box-shadow 250ms ease',
        '&:hover, &:focus-visible': {
          borderColor: 'primary.main',
          transform: 'translateY(-3px)',
          boxShadow: 3,
        },
        // The lift is decoration; the border change carries the state for
        // anyone who has asked not to be moved.
        '@media (prefers-reduced-motion: reduce)': {
          '&:hover, &:focus-visible': { transform: 'none' },
        },
        // The arrow tracks the card's hover, so it cannot be hovered alone.
        '&:hover .ServiceCard-arrow, &:focus-visible .ServiceCard-arrow': {
          transform: 'translateX(4px)',
          color: 'primary.main',
        },
      }}
    >
      <IconBadge name={service.icon} size={44} tone="tint" />

      <Stack spacing={2} sx={{ flex: 1 }}>
        <Typography variant="h6" component={level} sx={{ fontSize: '1.0625rem', lineHeight: 1.3 }}>
          {service.title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {service.tagline}
        </Typography>
        {detailed && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
              {service.summary}
            </Typography>
            {/* The outcome, marked as the one line that is a result rather
                than a description. The rule is the same as the cost chip's:
                say the thing the reader is deciding on, not another sentence
                about the thing they are deciding about. */}
            <Typography variant="body2" sx={{ pt: 1, fontWeight: 600, color: 'text.primary' }}>
              {service.outcome}
            </Typography>
          </>
        )}
      </Stack>

      <Divider />

      <Stack
        direction="row"
        spacing={3}
        sx={{ alignItems: 'center', justifyContent: 'space-between' }}
      >
        <ServiceCostChip cost={service.cost} />

        <Stack
          direction="row"
          spacing={2}
          sx={{ alignItems: 'center', flexShrink: 0, color: 'text.secondary' }}
        >
          <Typography variant="button" sx={{ fontSize: '0.8125rem', textTransform: 'none' }}>
            What this covers
          </Typography>
          <ArrowForwardRounded
            aria-hidden
            className="ServiceCard-arrow"
            sx={{ fontSize: 18, transition: 'transform 250ms ease, color 250ms ease' }}
          />
        </Stack>
      </Stack>
    </Box>
  );
}
